import { useCallback } from 'react';
import { useNotify } from 'react-admin';
import { useMutation } from '@tanstack/react-query';
import logger from '../../utils/logger';
/**
 * Custom hook for upload business logic and processing mutations
 */
export const useUploadLogic = (apiConfig, auth) => {
  const notify = useNotify();

  // Background processing mutation
  const { mutateAsync: processingMutateAsync } = useMutation({
    mutationFn: async (batchId) => {
      logger.log('⚡ About to call start endpoint with batchId:', batchId);
      
      if (!batchId) {
        logger.error('CRITICAL: batchId is null/undefined when calling start endpoint!');
        throw new Error('batchId is required for start processing');
      }
      
      const startUrl = `${apiConfig.getApiUrl('leadImports')}/lead-imports/${batchId}/start`;
      
      const startProcessingResponse = await fetch(startUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.getIdToken()}`,
        },
      });

      if (!startProcessingResponse.ok) {
        const body = await startProcessingResponse.json();
        throw new Error(`Failed to start processing: ${body.message}`);
      }

      return startProcessingResponse.json();
    },
    onSuccess: (data) => {
      const isQueued = data?.processingStatus === 'QUEUED';
      notify(
        isQueued
          ? 'Upload queued - processing will start automatically.'
          : 'Processing started successfully!',
        {
          type: 'success',
          autoHideDuration: 5000,
        },
      );
    },
    onError: (error) => {
      logger.error('Background processing error:', error);
      notify(`Processing failed: ${error.message}`, {
        type: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const performRefreshSequence = useCallback(async (onSuccess) => {
    if (!onSuccess) return;

    logger.log('Starting refresh sequence');

    // First refresh immediately
    await onSuccess();

    // Sequential refreshes to ensure data is updated
    setTimeout(async () => {
      logger.log('First delayed refresh');
      await onSuccess();

      setTimeout(async () => {
        logger.log('Second delayed refresh');
        await onSuccess();

        setTimeout(async () => {
          logger.log('Final delayed refresh');
          await onSuccess();
        }, 3000);
      }, 2000);
    }, 1000);
  }, []);

  const handleUploadSuccess = useCallback(async (batchId, onSuccess) => {
    // Start the refresh sequence
    await performRefreshSequence(onSuccess);

    // Start background processing (non-blocking)
    logger.log('🔄 Starting background processing for batchId:', batchId);
    processingMutateAsync(batchId).catch((error) => {
      logger.error('Background processing error:', error);
    });
  }, [performRefreshSequence, processingMutateAsync, notify]);

  return {
    handleUploadSuccess,
  };
};
